"use client";

import { motion, useAnimate, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";

import {
  bolivianCustomerPhoneSchema,
  toLocalBolivianPhoneInput,
} from "@/domain/order/order-intent";

export interface CustomerPhoneFieldProps {
  readonly onChange: (value: string) => void;
  readonly shakeSignal: number;
  readonly value: string;
}

export function CustomerPhoneField({
  onChange,
  shakeSignal,
  value,
}: Readonly<CustomerPhoneFieldProps>) {
  const [isTouched, setIsTouched] = useState(false);
  const [scope, animate] = useAnimate();
  const shouldReduceMotion = useReducedMotion();
  const isValid = bolivianCustomerPhoneSchema.safeParse(value).success;
  const hasPressError = shakeSignal > 0;
  const shouldShowError = (hasPressError || isTouched || value.length > 0) && !isValid;

  useEffect(() => {
    if (shakeSignal === 0 || shouldReduceMotion) {
      return;
    }

    void animate(
      scope.current,
      { x: [0, -8, 8, -6, 6, -3, 3, 0] },
      { duration: 0.5, ease: "easeInOut" },
    );
  }, [animate, scope, shakeSignal, shouldReduceMotion]);

  return (
    <label className="block text-sm font-bold text-muted">
      Tu número de WhatsApp
      <motion.span
        ref={scope}
        className={`mt-2 flex min-h-12 overflow-hidden rounded-lg border bg-surface focus-within:ring-1 ${
          shouldShowError
            ? "border-error focus-within:border-error focus-within:ring-error shadow-[0_0_0_4px_rgba(239,68,68,0.35)]"
            : "border-outline-variant focus-within:border-primary focus-within:ring-primary"
        }`}
      >
        <span className="inline-flex items-center border-r border-outline-variant bg-surface-container px-3 text-sm font-extrabold text-foreground">
          +591
        </span>
        <input
          aria-describedby={
            shouldShowError
              ? "customer-phone-help customer-phone-error"
              : "customer-phone-help"
          }
          aria-invalid={shouldShowError}
          autoComplete="tel-national"
          className="min-w-0 flex-1 bg-transparent px-3 text-base font-bold text-foreground outline-none placeholder:font-normal placeholder:text-outline-variant"
          inputMode="numeric"
          maxLength={12}
          name="customerPhone"
          onBlur={() => setIsTouched(true)}
          onChange={(event) => {
            onChange(toLocalBolivianPhoneInput(event.target.value));
          }}
          pattern="[0-9]{8}"
          placeholder="70000000"
          required
          type="tel"
          value={value}
        />
      </motion.span>
      <span id="customer-phone-help" className="mt-1 block text-xs font-normal text-muted">
        Ingresa los 8 dígitos de tu celular.
      </span>
      {shouldShowError ? (
        <span id="customer-phone-error" className="mt-1 block text-xs font-bold text-error">
          Ingresa un número válido de 8 dígitos.
        </span>
      ) : null}
    </label>
  );
}
